import { differenceInMinutes } from 'date-fns';

import { parseDateInTimezone } from 'helpers/intl-fns';

import { SEGMENTED_CAPACITY_BAR_SEGMENT } from '../Components/SegmentedCapacityBar';

export const SCHEDULE_LIST_V2_PAST_START_THRESHOLD_MINUTES = 30;

export const SCHEDULE_LIST_V2_FILL_BAR_VARIANT = {
  INTEGRATED: 'integrated',
  MANUAL: 'manual',
};

export const SCHEDULE_LIST_V2_STATUS_BADGE = {
  NEW_CLIENTS: 'newClients',
  SMARTSPOT_OFF: 'smartspotOff',
  WAITING: 'waiting',
};

export const SCHEDULE_LIST_V2_ADD_SPOT_TYPE = {
  ADD_SPOT: 'addspot',
  OPPORTUNITY: 'opportunity',
};

const FILL_BAR_EMPTY_COUNT = 0;
const ADD_SPOT_DEFAULT_COUNT = 1;

const ADD_SPOT_TYPE_ALIASES = {
  addspot: SCHEDULE_LIST_V2_ADD_SPOT_TYPE.ADD_SPOT,
  addssspot: SCHEDULE_LIST_V2_ADD_SPOT_TYPE.ADD_SPOT,
  opportunity: SCHEDULE_LIST_V2_ADD_SPOT_TYPE.OPPORTUNITY,
};

export function isScheduleListV2InThePast({ now = new Date(), schedule }) {
  const { startDate, startTime, startDateTime, venueTimezone } = schedule;

  const startInTimezone = venueTimezone
    ? parseDateInTimezone(`${startDate} ${startTime}`, venueTimezone)
    : startDateTime;

  return differenceInMinutes(now, startInTimezone) > SCHEDULE_LIST_V2_PAST_START_THRESHOLD_MINUTES;
}

export function getScheduleListV2AdminBadgeMessage({ isCancelled, isDisabled, reservationsStopped }) {
  if (reservationsStopped) {
    return 'reservationsStopped';
  }

  if (isCancelled) {
    return 'isCancelled';
  }

  if (isDisabled) {
    return 'isDisabled';
  }

  return null;
}

function toCount(value) {
  const numeric = Number(value);

  if (!Number.isFinite(numeric) || numeric < FILL_BAR_EMPTY_COUNT) {
    return FILL_BAR_EMPTY_COUNT;
  }

  return numeric;
}

function buildFillBarModel({ classpassAllocated, classpassBooked, directBooked, unfilled, variant }) {
  const classpassAllocatedCount = toCount(classpassAllocated);
  const classpassBookedCount = toCount(classpassBooked);
  const directBookedCount = toCount(directBooked);
  const unfilledCount = toCount(unfilled);
  const isIntegrated = variant === SCHEDULE_LIST_V2_FILL_BAR_VARIANT.INTEGRATED;
  const classpassIsFull =
    classpassAllocatedCount > FILL_BAR_EMPTY_COUNT && classpassBookedCount >= classpassAllocatedCount;

  return {
    classpassAllocated: classpassAllocatedCount,
    classpassBooked: classpassBookedCount,
    directBooked: directBookedCount,
    segments: isIntegrated
      ? [
          { key: SEGMENTED_CAPACITY_BAR_SEGMENT.DIRECT, value: directBookedCount },
          { key: SEGMENTED_CAPACITY_BAR_SEGMENT.CLASSPASS, value: classpassBookedCount },
          { key: SEGMENTED_CAPACITY_BAR_SEGMENT.UNFILLED, value: unfilledCount },
        ]
      : [
          { key: SEGMENTED_CAPACITY_BAR_SEGMENT.CLASSPASS, value: classpassBookedCount },
          { key: SEGMENTED_CAPACITY_BAR_SEGMENT.UNFILLED, value: unfilledCount },
        ],
    showClasspassFullTooltip: isIntegrated ? classpassIsFull && unfilledCount > FILL_BAR_EMPTY_COUNT : classpassIsFull,
    variant,
  };
}

function getFillBarFromPresentation(fillBar, { hasIntegrationData }) {
  if (!fillBar) {
    return null;
  }

  const { capacityTotal, classpassAllocated, classpassBooked, directBooked, unfilled } = fillBar;
  const hasAnyValue = [capacityTotal, classpassAllocated, classpassBooked, directBooked, unfilled].some(
    value => value != null
  );

  if (!hasAnyValue) {
    return null;
  }

  const variant = hasIntegrationData
    ? SCHEDULE_LIST_V2_FILL_BAR_VARIANT.INTEGRATED
    : SCHEDULE_LIST_V2_FILL_BAR_VARIANT.MANUAL;
  const classpassAllocatedCount = toCount(classpassAllocated);
  const classpassBookedCount = toCount(classpassBooked);
  let unfilledCount = toCount(unfilled);

  if (unfilled == null) {
    unfilledCount =
      variant === SCHEDULE_LIST_V2_FILL_BAR_VARIANT.INTEGRATED
        ? Math.max(FILL_BAR_EMPTY_COUNT, toCount(capacityTotal) - toCount(directBooked) - classpassBookedCount)
        : Math.max(FILL_BAR_EMPTY_COUNT, classpassAllocatedCount - classpassBookedCount);
  }

  return buildFillBarModel({
    classpassAllocated: classpassAllocatedCount,
    classpassBooked: classpassBookedCount,
    directBooked: hasIntegrationData ? toCount(directBooked) : FILL_BAR_EMPTY_COUNT,
    unfilled: unfilledCount,
    variant,
  });
}

function getFillBarFromSpots(schedule = {}) {
  const { hasIntegrationData, spots = {} } = schedule;
  const { classpassBooked, classpassTotal, maxCapacity, takenSpots, totalBooked } = spots;
  const classpassAllocatedCount = toCount(classpassTotal);
  const classpassBookedCount = toCount(classpassBooked);
  const takenSpotsCount = takenSpots == null ? classpassBookedCount : toCount(takenSpots);
  const classpassOccupiedCount =
    classpassAllocatedCount > FILL_BAR_EMPTY_COUNT
      ? Math.min(Math.max(classpassBookedCount, takenSpotsCount), classpassAllocatedCount)
      : Math.max(classpassBookedCount, takenSpotsCount);

  if (hasIntegrationData) {
    const totalBookedCount = toCount(totalBooked);

    return buildFillBarModel({
      classpassAllocated: classpassAllocatedCount,
      classpassBooked: classpassBookedCount,
      directBooked: Math.max(FILL_BAR_EMPTY_COUNT, totalBookedCount - classpassBookedCount),
      unfilled: Math.max(FILL_BAR_EMPTY_COUNT, toCount(maxCapacity) - totalBookedCount),
      variant: SCHEDULE_LIST_V2_FILL_BAR_VARIANT.INTEGRATED,
    });
  }

  return buildFillBarModel({
    classpassAllocated: classpassAllocatedCount,
    classpassBooked: classpassOccupiedCount,
    directBooked: FILL_BAR_EMPTY_COUNT,
    unfilled: Math.max(FILL_BAR_EMPTY_COUNT, classpassAllocatedCount - classpassOccupiedCount),
    variant: SCHEDULE_LIST_V2_FILL_BAR_VARIANT.MANUAL,
  });
}

export function getScheduleListV2FillBar(schedule) {
  const presentationFillBar = getFillBarFromPresentation(schedule?.listPresentation?.fillBar, {
    hasIntegrationData: schedule?.hasIntegrationData,
  });

  if (presentationFillBar) {
    return presentationFillBar;
  }

  return getFillBarFromSpots(schedule);
}

function getPositiveCount(value) {
  if (value == null) {
    return null;
  }

  const count = toCount(value);

  return count > FILL_BAR_EMPTY_COUNT ? count : null;
}

export function getScheduleListV2StatusBadges(schedule = {}) {
  const presentationBadges = schedule.listPresentation?.badges;
  const badges = [];
  const newClientsCount = getPositiveCount(presentationBadges?.newClientsCount);
  const waitingCount = getPositiveCount(presentationBadges?.waitingCount);

  if (newClientsCount != null) {
    badges.push({
      count: newClientsCount,
      key: SCHEDULE_LIST_V2_STATUS_BADGE.NEW_CLIENTS,
    });
  }

  if (waitingCount != null) {
    badges.push({
      count: waitingCount,
      key: SCHEDULE_LIST_V2_STATUS_BADGE.WAITING,
    });
  }

  if (schedule.smartspotEnabledForVenue === true && presentationBadges?.smartspotOffForSchedule === true) {
    badges.push({
      key: SCHEDULE_LIST_V2_STATUS_BADGE.SMARTSPOT_OFF,
    });
  }

  return badges;
}

export function getScheduleListV2AddSpot(schedule = {}, { canManageSpots = false } = {}) {
  if (!canManageSpots) {
    return null;
  }

  const addSpot = schedule.listPresentation?.addSpot;

  if (!addSpot || addSpot.visible !== true) {
    return null;
  }

  const normalizedType = ADD_SPOT_TYPE_ALIASES[String(addSpot.type || '').toLowerCase()];

  if (!normalizedType) {
    return null;
  }

  const spotsToAdd = getPositiveCount(addSpot.spotsToAdd) || ADD_SPOT_DEFAULT_COUNT;

  return {
    spotsToAdd,
    type: normalizedType,
  };
}

export function getScheduleListV2StatusRow(schedule = {}, { canManageSpots = false, isPast = false } = {}) {
  if (isPast) {
    return {
      addSpot: null,
      badges: [],
      show: false,
    };
  }

  const badges = getScheduleListV2StatusBadges(schedule);
  const addSpot = getScheduleListV2AddSpot(schedule, { canManageSpots });

  return {
    addSpot,
    badges,
    show: badges.length > FILL_BAR_EMPTY_COUNT || addSpot != null,
  };
}
